import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import {
  VaAlert,
  VaLinkAction,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import {
  aOrAn,
  getType2FocusedIds,
  markType2IdsAsFocused,
} from '../utils/helpers';
import { recordType2FailureEvent } from '../utils/analytics';
import {
  getIsSensitive,
  REQUEST_FOR_EVIDENCE_LABEL,
} from '../utils/trackedItemContent';

const HEADING = 'We need you to submit files by mail or in person';

function UploadType2ErrorAlert({
  failedSubmissions,
  isStatusPage,
  trackedItems = [],
}) {
  const alertRef = useRef(null);

  // Record Type 2 failure event every time component mounts with failed submissions
  // Only fires on status page
  useEffect(() => {
    if (failedSubmissions?.length > 0 && isStatusPage) {
      recordType2FailureEvent({ count: 1 });
    }
  }, [failedSubmissions, isStatusPage]);

  useEffect(() => {
    if (!failedSubmissions?.length) return undefined;

    const currentIds = failedSubmissions.map(s => s.id);
    const alreadySeen = getType2FocusedIds();
    // Only steal focus for IDs that haven't been focused this session.
    // This fires exactly once per new failure (satisfying AC 9 on any tab)
    // and never again on subsequent remounts caused by tab navigation,
    // eliminating focus theft for the full 30-day failure window.
    if (currentIds.every(id => alreadySeen.has(id))) return undefined;

    // Mark before the timer so a rapid remount in the same cycle
    // doesn't schedule a second competing timer.
    markType2IdsAsFocused(currentIds);

    const timerId = setTimeout(() => {
      const alertHeadline = alertRef.current?.querySelector('h2');
      const target = alertHeadline || alertRef.current;

      if (target) {
        target.setAttribute('tabindex', '-1');
        target.focus();
      }
    }, 150);

    return () => clearTimeout(timerId);
  }, [failedSubmissions]);

  // Don't render anything if there are no failed submissions
  if (!failedSubmissions || failedSubmissions.length === 0) {
    return null;
  }

  // Sort submissions by failedDate (most recent first)
  const sortedSubmissions = [...failedSubmissions].sort((a, b) => {
    const dateA = new Date(a.failedDate || 0);
    const dateB = new Date(b.failedDate || 0);
    return dateB - dateA;
  });

  const mostRecent = sortedSubmissions[0];
  const count = sortedSubmissions.length;
  const isSingle = count === 1;

  // Fail closed: only name the request when we have a matching tracked item in the claim.
  // An unmatched trackedItemId drops the clause entirely. Sensitive items still render the
  // clause; the friendlyName is masked from DD session replay via data-dd-privacy on the
  // <strong> below.
  const matchedTrackedItem = trackedItems.find(
    ti => ti.id === mostRecent.trackedItemId,
  );
  const showTrackedItemClause = !!matchedTrackedItem;
  const isSensitive =
    showTrackedItemClause && getIsSensitive(matchedTrackedItem);
  const requestLabel =
    matchedTrackedItem?.friendlyName || REQUEST_FOR_EVIDENCE_LABEL;
  // Avoid "Request for evidence request" when we fall back to the generic label.
  const requestSuffix =
    requestLabel === REQUEST_FOR_EVIDENCE_LABEL ? '' : ' request';

  const file = isSingle
    ? { noun: 'file', indefinite: 'a file', demonstrative: 'this file' }
    : { noun: 'files', indefinite: 'files', demonstrative: 'these files' };

  const introClause = showTrackedItemClause ? (
    <>
      {' '}
      when you submitted {file.indefinite} for {aOrAn(requestLabel)}{' '}
      <strong data-dd-privacy={isSensitive ? 'mask' : undefined}>
        {requestLabel}
      </strong>
      {requestSuffix}
    </>
  ) : null;

  const youSubmittedClause = showTrackedItemClause ? '' : ' you submitted';
  const mostRecentSuffix = isSingle ? ':' : '. This is the most recent file:';
  const retryVerb = showTrackedItemClause ? 'submit' : 'resubmit';
  const documentType = mostRecent.documentType || 'Unknown';
  const docTypeText = showTrackedItemClause
    ? documentType
    : `File type: ${documentType}`;

  const body = (
    <>
      <p>We’re sorry. There was a problem with our system{introClause}.</p>
      <p>
        We couldn’t process {count} {file.noun}
        {youSubmittedClause} within the last 30 days{mostRecentSuffix}
      </p>
      {/* add explicit role=list to expose the <ul> as a list for Safari/VoiceOver */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ul
        className="usa-unstyled-list"
        role="list"
        aria-label={
          isSingle
            ? "File that couldn't be processed"
            : "Files that couldn't be processed"
        }
      >
        <li key={mostRecent.id}>
          <p className="vads-u-margin-bottom--0">
            <strong
              data-dd-privacy="mask"
              data-dd-action-name="type 2 error filename"
            >
              {mostRecent.fileName}
            </strong>
          </p>
          <p className="vads-u-margin-top--0">{docTypeText}</p>
        </li>
      </ul>
      <p>
        You’ll need to print and submit {file.demonstrative} by mail or in
        person. The problem may happen again if you try to {retryVerb}{' '}
        {file.indefinite} here.
      </p>
      <p>
        If you already submitted {file.demonstrative} another way, you can
        ignore this message. We’ll remove this alert 30 days after the upload
        failed.
      </p>
      <VaLinkAction
        href="/track-claims/your-claims/files-we-couldnt-receive"
        text="Review files we couldn’t process and learn how to resubmit"
        type="primary"
      />
    </>
  );

  return (
    <VaAlert
      ref={alertRef}
      data-testid="notification"
      close-btn-aria-label="Close notification"
      className="evidence-submission-type-2-alert vads-u-margin-y--4"
      closeable={false}
      status="error"
      visible
    >
      <h2 className="usa-alert-heading vads-u-font-size--h3">{HEADING}</h2>
      <div className="vads-u-margin-y--0">{body}</div>
    </VaAlert>
  );
}

UploadType2ErrorAlert.propTypes = {
  failedSubmissions: PropTypes.array,
  isStatusPage: PropTypes.bool,
  trackedItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      isSensitive: PropTypes.bool,
    }),
  ),
};

export default UploadType2ErrorAlert;
